import client from '../../shared/api/client';
import {
  DisputeMessage,
  DisputeRecord,
  DisputeResolution,
  P2pDispute,
  PaginatedResponse,
  TradeChatMessage,
} from '../../shared/types';

interface P2pBackendResponse<T> {
  status: string;
  data: T;
}

export interface P2pTradesFilters {
  page?: number;
  per_page?: number;
  status?: string;
  user_id?: string;
  mode?: 'p2p' | 'otc' | '';
}

export interface DisputesFilters {
  page?: number;
  per_page?: number;
  status?: string;
  priority?: string;
  assigned_admin_id?: number;
  user_id?: string;
  mode?: 'p2p' | 'otc';
}

export const getDisputes = async (
  filters: DisputesFilters = {}
): Promise<PaginatedResponse<DisputeRecord>> => {
  const params: Record<string, string | number | undefined> = {
    page: Number(filters.page ?? 1),
    per_page: Number(filters.per_page ?? 20),
  };
  if (filters.status) params.status = filters.status;
  if (filters.priority) params.priority = filters.priority;
  if (filters.assigned_admin_id) params.assigned_admin_id = filters.assigned_admin_id;
  if (filters.user_id) params.user_id = filters.user_id;
  if (filters.mode) params.mode = filters.mode;
  const response = await client.get<P2pBackendResponse<PaginatedResponse<DisputeRecord>>>(
    '/p2p/disputes',
    { params }
  );
  const payload = response.data?.data;
  if (payload && Array.isArray(payload.data) && typeof payload.current_page === 'number') {
    return payload;
  }
  return {
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: params.per_page as number,
    total: 0,
  };
};

export const getDispute = async (id: number): Promise<DisputeRecord> => {
  const response = await client.get<P2pBackendResponse<DisputeRecord>>(`/p2p/disputes/${id}`);
  return response.data.data;
};

export const getDisputeMessages = async (id: number): Promise<DisputeMessage[]> => {
  const response = await client.get<P2pBackendResponse<DisputeMessage[]>>(
    `/p2p/disputes/${id}/messages`
  );
  return response.data.data;
};

export const sendDisputeMessage = async (
  id: number,
  data: { message: string; is_internal?: boolean; attachments?: Array<{ url: string }> }
): Promise<DisputeMessage> => {
  const response = await client.post<P2pBackendResponse<DisputeMessage>>(
    `/p2p/disputes/${id}/messages`,
    data
  );
  return response.data.data;
};

export const getDisputeImageUrl = async (id: number, path: string): Promise<string> => {
  const response = await client.get(`/p2p/disputes/${id}/image`, {
    params: { path },
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
};

export const getCashImageUrl = async (tradeId: number, path: string): Promise<string> => {
  const response = await client.get(`/p2p/trades/${tradeId}/cash/image`, {
    params: { path },
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
};

export const getTradeChat = async (id: number): Promise<TradeChatMessage[]> => {
  const response = await client.get<P2pBackendResponse<TradeChatMessage[]>>(
    `/p2p/disputes/${id}/trade-chat`
  );
  return response.data.data;
};

export const getTradeChatImageUrl = async (id: number, messageId: number): Promise<string> => {
  const response = await client.get(`/p2p/disputes/${id}/trade-chat/image/${messageId}`, {
    responseType: 'blob',
  });
  return URL.createObjectURL(response.data);
};

export const assignDispute = async (id: number): Promise<DisputeRecord> => {
  const response = await client.post<P2pBackendResponse<DisputeRecord>>(
    `/p2p/disputes/${id}/assign`,
    {}
  );
  return response.data.data;
};

export const unassignDispute = async (id: number): Promise<DisputeRecord> => {
  const response = await client.post<P2pBackendResponse<DisputeRecord>>(
    `/p2p/disputes/${id}/unassign`,
    {}
  );
  return response.data.data;
};

export const requestDisputeEvidence = async (
  id: number,
  data: { from: 'buyer' | 'seller'; note: string }
): Promise<DisputeRecord> => {
  const response = await client.post<P2pBackendResponse<DisputeRecord>>(
    `/p2p/disputes/${id}/request-evidence`,
    data
  );
  return response.data.data;
};

export const getTrades = async (
  filters: P2pTradesFilters = {}
): Promise<PaginatedResponse<P2pDispute>> => {
  const params: Record<string, string | number | undefined> = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 20,
  };
  if (filters.status) params.status = filters.status;
  if (filters.user_id) params.user_id = filters.user_id;
  if (filters.mode) params.mode = filters.mode;
  const response = await client.get<P2pBackendResponse<PaginatedResponse<P2pDispute>>>(
    '/p2p/trades',
    { params }
  );
  return response.data.data;
};

export const resolveDispute = async (
  id: number,
  data: { outcome: DisputeResolution; reason?: string }
): Promise<unknown> => {
  const response = await client.post(`/p2p/disputes/${id}/resolve`, data);
  return response.data;
};

export const forceReleaseTrade = async (
  id: number,
  data?: { reason?: string }
): Promise<unknown> => {
  const response = await client.post(`/p2p/trades/${id}/force-release`, data ?? {});
  return response.data;
};
